import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Bot, X, Send, Loader2, Sparkles, Paperclip } from "lucide-react";
import { useAssistant } from "./assistant/useAssistant";
import { EstimatorForm } from "./assistant/EstimatorForm";
import { ExpertPanel } from "./assistant/ExpertPanel";
import { T, LANGS, QUICK_ACTIONS } from "./assistant/i18n";

const TypingDots = () => (
  <div className="flex gap-1 px-1 py-1.5" data-testid="assistant-typing">
    {[0, 1, 2].map((i) => (
      <span key={i} className="h-2 w-2 animate-bounce rounded-full bg-navy/40" style={{ animationDelay: `${i * 0.15}s` }} />
    ))}
  </div>
);

export const AssistantWidget = () => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("chat");
  const [input, setInput] = useState("");
  const { sessionId, config, messages, streaming, send, analyzeFile, loadHistory, language, setLanguage } = useAssistant();
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const t = T[language] || T.en;

  useEffect(() => { if (open) loadHistory(); }, [open, loadHistory]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, view, streaming]);

  const handleQuick = (a) => {
    if (a.key === "quote") return setView("estimate");
    if (a.key === "expert") return setView("expert");
    if (a.key === "emergency") return setView("emergency");
    setView("chat");
    send(a.msg || a.en);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setView("chat");
    send(input);
    setInput("");
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) { setView("chat"); analyzeFile(f); }
    e.target.value = "";
  };

  const showWelcome = messages.length === 0;

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 z-[60] flex h-[min(600px,80vh)] w-[min(380px,calc(100vw-2rem))] flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl shadow-black/30 sm:right-6"
            data-testid="assistant-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between bg-navy px-4 py-3.5">
              <div className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brandred text-white">
                  <Bot size={18} />
                </span>
                <div>
                  <p className="font-display text-sm font-black leading-none text-white">ROK Assistant</p>
                  <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-white/70">
                    <span className={`h-1.5 w-1.5 rounded-full ${config?.is_open ? "bg-green-400" : "bg-amber-400"}`} />
                    {config?.is_open ? t.online : t.offline}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex overflow-hidden rounded-full bg-white/10" data-testid="assistant-lang">
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => setLanguage(l.code)}
                      data-testid={`lang-${l.code}`}
                      className={`px-2 py-1 text-[11px] font-bold transition-colors ${language === l.code ? "bg-gold text-navy" : "text-white/70 hover:text-white"}`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
                <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white" aria-label="Close" data-testid="assistant-close">
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Body */}
            {view === "estimate" ? (
              <div className="flex-1 overflow-y-auto"><EstimatorForm onBack={() => setView("chat")} /></div>
            ) : view === "expert" || view === "emergency" ? (
              <div className="flex-1 overflow-y-auto">
                <ExpertPanel onBack={() => setView("chat")} config={config} sessionId={sessionId} emergency={view === "emergency"} />
              </div>
            ) : (
              <>
                <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-navy-50/40 px-3.5 py-4" data-testid="assistant-messages">
                  {showWelcome && (
                    <div className="rounded-2xl rounded-tl-sm bg-white p-3.5 text-sm text-navy shadow-sm">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{t.welcome}</ReactMarkdown>
                    </div>
                  )}
                  {messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm shadow-sm ${
                          m.role === "user" ? "rounded-br-sm bg-brandred text-white" : "rounded-tl-sm bg-white text-navy"
                        }`}
                        data-testid={`assistant-msg-${m.role}`}
                      >
                        {m.role === "assistant" && !m.content && streaming ? (
                          <TypingDots />
                        ) : m.role === "assistant" ? (
                          <div className="assistant-md prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                          </div>
                        ) : (
                          m.content
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Quick actions */}
                {(showWelcome || !streaming) && (
                  <div className="flex flex-wrap gap-1.5 border-t border-gray-100 bg-white px-3 py-2.5" data-testid="assistant-quick-actions">
                    {QUICK_ACTIONS.map((a) => (
                      <button
                        key={a.key}
                        onClick={() => handleQuick(a)}
                        data-testid={`quick-action-${a.key}`}
                        className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                          a.key === "emergency"
                            ? "border-brandred/30 bg-brandred/5 text-brandred hover:bg-brandred hover:text-white"
                            : "border-navy/15 text-navy hover:border-navy hover:bg-navy hover:text-white"
                        }`}
                      >
                        {a[language] || a.en}
                      </button>
                    ))}
                  </div>
                )}

                {/* Input */}
                <form onSubmit={submit} className="flex items-center gap-2 border-t border-gray-100 bg-white p-3">
                  <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.csv" onChange={onFile} className="hidden" data-testid="assistant-file-input" />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={streaming}
                    aria-label={t.attach}
                    title={t.attach}
                    data-testid="assistant-attach"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-200 text-navy transition-colors hover:bg-navy-50 disabled:opacity-50"
                  >
                    <Paperclip size={17} />
                  </button>
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={t.placeholder}
                    data-testid="assistant-input"
                    className="flex-1 rounded-full border border-gray-200 px-4 py-2.5 text-sm outline-none focus:border-navy"
                  />
                  <button
                    type="submit"
                    disabled={streaming || !input.trim()}
                    data-testid="assistant-send"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brandred text-white transition-transform hover:bg-brandred-700 active:scale-90 disabled:opacity-50"
                  >
                    {streaming ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating button */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Open ROK Assistant"
        data-testid="assistant-fab"
        className="group fixed bottom-6 right-4 z-[60] flex items-center gap-2 rounded-full bg-navy px-4 py-4 text-white shadow-xl shadow-navy/40 transition-transform hover:scale-105 active:scale-95 sm:right-6"
      >
        {!open && <span className="absolute inset-0 animate-ping rounded-full bg-navy opacity-30" />}
        {open ? <X className="relative h-6 w-6" /> : <Bot className="relative h-6 w-6" />}
        {!open && (
          <span className="relative hidden items-center gap-1 whitespace-nowrap text-sm font-bold sm:flex">
            <Sparkles size={14} className="text-gold" /> Ask ROK Assistant
          </span>
        )}
      </button>
    </>
  );
};
